import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Wheat, Store, Package, Cherry, Carrot, Leaf } from 'lucide-react';
import { getListingsByType } from '../../data';

const categoryConfig = [
  {
    id: 1,
    name: 'Farms',
    slug: 'farms',
    type: 'farm',
    icon: Wheat,
    description: 'Local farms, orchards & ranches'
  },
  {
    id: 2,
    name: 'Farmers Markets',
    slug: 'farmers-markets',
    type: 'market',
    icon: Store,
    description: 'Weekly community markets'
  },
  {
    id: 3,
    name: 'CSA Programs',
    slug: 'csa-programs',
    type: 'csa',
    icon: Package,
    description: 'Farm subscription boxes'
  },
  {
    id: 4,
    name: 'U-Pick',
    slug: 'u-pick',
    type: 'upick',
    icon: Cherry,
    description: 'Pick your own farms'
  },
  {
    id: 5,
    name: 'Farm Stands',
    slug: 'farm-stands',
    type: 'farmstand',
    icon: Carrot,
    description: 'Roadside stands & stores'
  },
  {
    id: 6,
    name: 'Organic Producers',
    slug: 'organic-producers',
    type: 'organic',
    icon: Leaf,
    description: 'Certified organic growers'
  }
];

const CategoryGrid = () => {
  const categories = useMemo(() => {
    return categoryConfig.map(cat => ({
      ...cat,
      count: getListingsByType(cat.type).length
    }));
  }, []);

  return (
    <section className="category-grid">
      <style>{`
        :root {
          --category-cream: #faf8f5;
          --category-sage: #e8efe5;
          --category-green-primary: #3d6b4f;
          --category-green-dark: #2d5a3f;
          --category-brown: #6b5344;
          --category-text-dark: #2c3e2d;
          --category-text-muted: #5a6b5c;
        }

        .category-grid {
          padding: 80px 0;
          background-color: var(--category-cream);
        }

        .category-grid__container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .category-grid__header {
          text-align: center;
          margin-bottom: 50px;
        }

        .category-grid__title {
          font-size: 2.5rem;
          font-weight: 700;
          color: var(--category-text-dark);
          margin-bottom: 12px;
        }

        .category-grid__subtitle {
          font-size: 1.1rem;
          color: var(--category-text-muted);
          max-width: 500px;
          margin: 0 auto;
        }

        .category-grid__cards {
          display: grid;
          grid-template-columns: 1fr;
          gap: 24px;
        }

        @media (min-width: 640px) {
          .category-grid__cards {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (min-width: 1024px) {
          .category-grid__cards {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        .category-card {
          display: block;
          background-color: var(--category-sage);
          border-radius: 16px;
          padding: 32px 24px;
          text-decoration: none;
          border: 2px solid transparent;
          transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;
          cursor: pointer;
        }

        .category-card:hover {
          transform: scale(1.03);
          box-shadow: 0 12px 32px rgba(61, 107, 79, 0.15);
          border-color: var(--category-green-primary);
        }

        .category-card__icon-wrapper {
          width: 72px;
          height: 72px;
          background-color: var(--category-cream);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 20px;
          transition: background-color 0.3s ease;
        }

        .category-card:hover .category-card__icon-wrapper {
          background-color: var(--category-green-primary);
        }

        .category-card__icon {
          width: 36px;
          height: 36px;
          color: var(--category-green-primary);
          transition: color 0.3s ease;
        }

        .category-card:hover .category-card__icon {
          color: #ffffff;
        }

        .category-card__name {
          font-size: 1.35rem;
          font-weight: 600;
          color: var(--category-text-dark);
          margin-bottom: 6px;
        }

        .category-card__count {
          font-size: 0.95rem;
          color: var(--category-green-primary);
          font-weight: 500;
          margin-bottom: 10px;
        }

        .category-card__description {
          font-size: 0.95rem;
          color: var(--category-text-muted);
          line-height: 1.5;
        }

        @media (max-width: 639px) {
          .category-grid {
            padding: 60px 0;
          }

          .category-grid__title {
            font-size: 2rem;
          }

          .category-card {
            padding: 24px 20px;
          }

          .category-card__icon-wrapper {
            width: 60px;
            height: 60px;
          }

          .category-card__icon {
            width: 28px;
            height: 28px;
          }
        }
      `}</style>

      <div className="category-grid__container">
        <div className="category-grid__header">
          <h2 className="category-grid__title">Explore Local Food Categories</h2>
          <p className="category-grid__subtitle">
            Discover fresh, locally-sourced food from farms and markets in your community
          </p>
        </div>

        <div className="category-grid__cards">
          {categories.map((category) => {
            const IconComponent = category.icon;
            return (
              <Link
                key={category.id}
                to={`/category/${category.slug}`}
                className="category-card"
              >
                <div className="category-card__icon-wrapper">
                  <IconComponent className="category-card__icon" />
                </div>
                <h3 className="category-card__name">{category.name}</h3>
                <p className="category-card__count">{category.count} {category.count === 1 ? 'listing' : 'listings'}</p>
                <p className="category-card__description">{category.description}</p>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default CategoryGrid;
